import { OnboardingFlagKey } from "@redotech/feature-flags-keys-onboarding/keys";
import {
  ComponentType,
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { TeamContext } from "../team";
import { applyWalkthroughModalSuppression } from "./walkthrough-modal-suppression";
import { WalkthroughOverlay } from "./walkthrough-overlay";
import { WalkthroughRegistryEntry } from "./walkthrough-registry-entry";
import {
  WalkthroughDefinition,
  WalkthroughHighlightStep,
  WalkthroughModalStep,
  WalkthroughStep,
} from "./walkthrough-types";

export type WalkthroughSignalHandler = (payload?: unknown) => void;

export interface WalkthroughContextValue {
  active: boolean;
  currentStep: WalkthroughStep | undefined;
  currentStepIndex: number;
  totalSteps: number;
  showMockData: boolean;
  /** Mock data source loaded by the active walkthrough's registry entry. */
  mockSource: unknown;
  /** Overlay component loaded by the active walkthrough's registry entry. */
  overlayComponent: ComponentType<any> | undefined;
  next(): void;
  back(): void;
  dismiss(): void;
  /** Imperatively start a walkthrough by id, bypassing the URL param. */
  start(walkthroughId: string): void;
  /** Fire a named signal to all subscribers. */
  emit(name: string, payload?: unknown): void;
  /** Subscribe to a named signal. Returns an unsubscribe function. */
  subscribe(name: string, handler: WalkthroughSignalHandler): () => void;
}

export const WalkthroughContext = createContext<
  WalkthroughContextValue | undefined
>(undefined);
WalkthroughContext.displayName = "WalkthroughContext";

const WALKTHROUGH_PARAM = "walkthrough";

function getWalkthroughEntry(
  registry: WalkthroughRegistryEntry[],
  id: string,
): WalkthroughRegistryEntry | undefined {
  return registry.find((entry) => entry.id === id);
}

type ResolvedStep = WalkthroughModalStep | WalkthroughHighlightStep;

function isRenderable(step: WalkthroughStep): step is ResolvedStep {
  return step.type !== "signal";
}

function computeRenderableInfo(
  steps: WalkthroughStep[],
  currentStepIndex: number,
): { index: number; total: number } {
  let total = 0;
  let index = 0;
  for (const [i, step] of steps.entries()) {
    if (!isRenderable(step!)) {
      continue;
    }
    if (i === currentStepIndex) {
      index = total;
    }
    if (!(step!.type === "modal" && step!.excludeFromStepCount)) {
      total++;
    }
  }
  return { index, total };
}

export const WalkthroughProvider = memo(function WalkthroughProvider({
  children,
  registry,
}: {
  children: ReactNode;
  registry: WalkthroughRegistryEntry[];
}) {
  const team = useContext(TeamContext);
  const teamLoaded = team !== undefined;
  const walkthroughEnabled =
    team?.featureFlags[OnboardingFlagKey.MERCHANT_WALKTHROUGH] === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const rawWalkthroughParam = searchParams.get(WALKTHROUGH_PARAM);
  // Wait for team to load before resolving — prevents the loader from
  // firing before we know the flag status, and avoids stripping the
  // param prematurely.
  const walkthroughName =
    teamLoaded && walkthroughEnabled ? rawWalkthroughParam : null;

  // Strip the URL param once team has loaded and the flag is confirmed off
  useEffect(() => {
    if (teamLoaded && !walkthroughEnabled && rawWalkthroughParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(WALKTHROUGH_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [teamLoaded, walkthroughEnabled, rawWalkthroughParam, setSearchParams]);

  const [definition, setDefinition] = useState<
    WalkthroughDefinition | undefined
  >(undefined);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loadedWalkthroughName, setLoadedWalkthroughName] = useState<
    string | undefined
  >(undefined);
  const [mockSource, setMockSource] = useState<unknown>(undefined);
  const [overlayComponent, setOverlayComponent] = useState<
    ComponentType<any> | undefined
  >(undefined);

  const subscribersRef = useRef<Map<string, Set<WalkthroughSignalHandler>>>(
    new Map(),
  );

  const subscribe = useCallback(
    (name: string, handler: WalkthroughSignalHandler) => {
      let set = subscribersRef.current.get(name);
      if (!set) {
        set = new Set();
        subscribersRef.current.set(name, set);
      }
      set.add(handler);
      return () => {
        const current = subscribersRef.current.get(name);
        if (!current) {
          return;
        }
        current.delete(handler);
        if (current.size === 0) {
          subscribersRef.current.delete(name);
        }
      };
    },
    [],
  );

  const emit = useCallback((name: string, payload?: unknown) => {
    const set = subscribersRef.current.get(name);
    if (!set) {
      return;
    }
    for (const handler of set) {
      handler(payload);
    }
  }, []);

  const loadWalkthrough = useCallback(
    (name: string): (() => void) => {
      const entry = getWalkthroughEntry(registry, name);
      if (!entry) {
        return () => {};
      }

      let cancelled = false;

      Promise.all([
        entry.loader(),
        entry.mockSourceLoader
          ? entry.mockSourceLoader()
          : Promise.resolve(undefined),
        entry.overlayLoader
          ? entry.overlayLoader()
          : Promise.resolve(undefined),
      ])
        .then(([defModule, mockModule, overlayModule]) => {
          if (cancelled) {
            return;
          }
          setDefinition(defModule.definition);
          setMockSource(mockModule?.source);
          // Wrap setter call so React doesn't treat the component as a state
          // initializer function and invoke it.
          setOverlayComponent(() => overlayModule?.Component);
          setCurrentStepIndex(0);
          setDismissed(false);
          setLoadedWalkthroughName(name);
        })
        .catch((err) => {
          console.error("Failed to load walkthrough:", name, err);
        });

      return () => {
        cancelled = true;
      };
    },
    [registry],
  );

  // Tracks the in-flight cleanup for a programmatically-started load so it can
  // be cancelled on unmount or when a new walkthrough is started.
  const startCleanupRef = useRef<(() => void) | undefined>(undefined);
  const start = useCallback(
    (walkthroughId: string) => {
      startCleanupRef.current?.();
      startCleanupRef.current = loadWalkthrough(walkthroughId);
    },
    [loadWalkthrough],
  );
  useEffect(() => () => startCleanupRef.current?.(), []);

  useEffect(() => {
    if (!walkthroughName) {
      // Don't clear an already-loaded walkthrough when React Router
      // navigation drops the search param from the URL.
      if (!loadedWalkthroughName) {
        setDefinition(undefined);
        setCurrentStepIndex(0);
        setDismissed(false);
      }
      return;
    }

    // Skip reloading if we already have this walkthrough loaded
    if (loadedWalkthroughName === walkthroughName) {
      return;
    }

    return loadWalkthrough(walkthroughName);
  }, [walkthroughName, loadedWalkthroughName, loadWalkthrough]);

  const removeParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(WALKTHROUGH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setLoadedWalkthroughName(undefined);
    setDefinition(undefined);
    setCurrentStepIndex(0);
    setMockSource(undefined);
    setOverlayComponent(undefined);
    removeParam();
  }, [removeParam]);

  const next = useCallback(() => {
    if (!definition) {
      return;
    }
    let nextIndex = currentStepIndex + 1;

    // Walk forward, skipping:
    //  - signal steps (non-renderable; emit their forward signal as we pass)
    //  - highlight steps with skipIfMissing=true whose target isn't in the
    //    DOM right now (e.g., sidebar views that don't render on empty teams)
    while (nextIndex < definition.steps.length) {
      const s = definition.steps[nextIndex]!;
      if (s.type === "signal") {
        if (s.forward) {
          emit(s.forward, s.payload);
        }
        nextIndex++;
        continue;
      }
      if (s.type === "highlight" && s.skipIfMissing) {
        try {
          if (!document.querySelector(s.targetSelector)) {
            nextIndex++;
            continue;
          }
        } catch {
          nextIndex++;
          continue;
        }
      }
      break;
    }

    if (nextIndex >= definition.steps.length) {
      dismiss();
      return;
    }

    setCurrentStepIndex(nextIndex);
  }, [definition, currentStepIndex, dismiss, emit]);

  const back = useCallback(() => {
    if (!definition) {
      return;
    }
    let prevIndex = currentStepIndex - 1;

    // Walk backwards, skipping steps we can't render:
    //  - signal steps (non-renderable; emit their backward signal as we pass)
    //  - highlight steps whose target element isn't in the DOM (e.g. inside
    //    a process modal that's no longer open)
    while (prevIndex >= 0) {
      const s = definition.steps[prevIndex]!;
      if (s.type === "signal") {
        if (s.backward) {
          emit(s.backward, s.payload);
        }
        prevIndex--;
        continue;
      }
      if (s.type === "highlight") {
        try {
          if (!document.querySelector(s.targetSelector)) {
            prevIndex--;
            continue;
          }
        } catch {
          prevIndex--;
          continue;
        }
      }
      break;
    }

    if (prevIndex < 0) {
      return;
    }
    setCurrentStepIndex(prevIndex);
  }, [definition, currentStepIndex, emit]);

  const currentRawStep = definition?.steps[currentStepIndex];
  const active = !!definition && !dismissed;

  useEffect(() => applyWalkthroughModalSuppression(active), [active]);

  const currentStep = active && currentRawStep ? currentRawStep : undefined;

  // Whether the active step's section requested mock data injection
  const showMockData = useMemo(() => {
    if (!active) {
      return false;
    }
    return !!currentStep?.section?.showMockData;
  }, [active, currentStep]);

  const renderableInfo = useMemo(
    () =>
      definition
        ? computeRenderableInfo(definition.steps, currentStepIndex)
        : { index: 0, total: 0 },
    [definition, currentStepIndex],
  );

  const resolved =
    currentStep && isRenderable(currentStep) ? currentStep : undefined;

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      active,
      currentStep,
      currentStepIndex,
      totalSteps: renderableInfo.total,
      showMockData,
      mockSource,
      overlayComponent,
      next,
      back,
      dismiss,
      start,
      emit,
      subscribe,
    }),
    [
      active,
      currentStep,
      currentStepIndex,
      renderableInfo.total,
      showMockData,
      mockSource,
      overlayComponent,
      next,
      back,
      dismiss,
      start,
      emit,
      subscribe,
    ],
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
      {active &&
        resolved &&
        createPortal(
          <WalkthroughOverlay
            back={back}
            dismiss={dismiss}
            next={next}
            step={resolved}
            stepIndex={renderableInfo.index}
            totalSteps={renderableInfo.total}
          />,
          document.body,
        )}
    </WalkthroughContext.Provider>
  );
});
